import { RoundCheckButton } from "@/components/RoundCheckButton";
import {
  useAddSessionItem,
  useCreateSession,
  useSession,
} from "@/hooks/use-shopping-session";
import { useTodos } from "@/hooks/use-todos";
import type { Todo } from "@/types/todo";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Tag } from "lucide-react";
import { useMemo, useState } from "react";

type PickSearch = {
  venues: string[];
  name?: string;
  sessionId?: string;
};

export const Route = createFileRoute("/_app/shopping/pick")({
  validateSearch: (search: Record<string, unknown>): PickSearch => ({
    venues: Array.isArray(search.venues) ? (search.venues as string[]) : [],
    name: typeof search.name === "string" ? search.name : undefined,
    sessionId:
      typeof search.sessionId === "string" ? search.sessionId : undefined,
  }),
  component: ShoppingPickPage,
});

type PickedItem = {
  todoId: string;
  venueName: string;
  selected: boolean;
};

function ShoppingPickPage() {
  const navigate = useNavigate();
  const { venues, name, sessionId } = Route.useSearch();
  const { data: allTodos, isLoading: todosLoading } = useTodos();
  const { data: existingSession } = useSession(sessionId ?? "");
  const createSession = useCreateSession();
  const addSessionItem = useAddSessionItem();
  const isAddMode = !!sessionId && !!existingSession;

  const existingTodoIds = useMemo(
    () => new Set(existingSession?.items.map((i) => i.todoId) ?? []),
    [existingSession?.items],
  );

  const uncheckedTodos = useMemo(
    () =>
      (allTodos ?? []).filter((t) => !t.checked && !existingTodoIds.has(t.id)),
    [allTodos, existingTodoIds],
  );

  const matchedTodos = useMemo(
    () =>
      uncheckedTodos.filter(
        (t) => t.suggestedVenue && venues.includes(t.suggestedVenue),
      ),
    [uncheckedTodos, venues],
  );

  const otherTodos = useMemo(
    () =>
      uncheckedTodos.filter(
        (t) => !t.suggestedVenue || !venues.includes(t.suggestedVenue),
      ),
    [uncheckedTodos, venues],
  );

  /** ユーザーが明示的に変更した選択状態・venue を保持する */
  const [userOverrides, setUserOverrides] = useState<
    Map<string, { selected?: boolean; venueName?: string }>
  >(new Map());

  /** todos データ + ユーザー操作から pickedItems を導出する */
  const pickedItems = useMemo(() => {
    const map = new Map<string, PickedItem>();
    for (const todo of matchedTodos) {
      const override = userOverrides.get(todo.id);
      map.set(todo.id, {
        todoId: todo.id,
        venueName: override?.venueName ?? todo.suggestedVenue ?? venues[0],
        selected: override?.selected ?? false,
      });
    }
    for (const todo of otherTodos) {
      const override = userOverrides.get(todo.id);
      map.set(todo.id, {
        todoId: todo.id,
        venueName: override?.venueName ?? todo.suggestedVenue ?? venues[0],
        selected: override?.selected ?? false,
      });
    }
    return map;
  }, [matchedTodos, otherTodos, userOverrides, venues]);

  const toggleItem = (todoId: string) => {
    setUserOverrides((prev) => {
      const next = new Map(prev);
      const current = next.get(todoId);
      const currentSelected = pickedItems.get(todoId)?.selected ?? false;
      next.set(todoId, { ...current, selected: !currentSelected });
      return next;
    });
  };

  const changeVenue = (todoId: string, venueName: string) => {
    setUserOverrides((prev) => {
      const next = new Map(prev);
      const current = next.get(todoId);
      next.set(todoId, { ...current, venueName });
      return next;
    });
  };

  const selectedItems = [...pickedItems.values()].filter((i) => i.selected);

  const handleStart = async () => {
    if (selectedItems.length === 0) return;

    if (isAddMode) {
      for (const item of selectedItems) {
        await addSessionItem.mutateAsync({
          sessionId,
          todoId: item.todoId,
          venueName: item.venueName,
        });
      }
      navigate({
        to: "/shopping/$sessionId",
        params: { sessionId },
      });
    } else {
      const session = await createSession.mutateAsync({
        name,
        venues,
        items: selectedItems.map(({ todoId, venueName }) => ({
          todoId,
          venueName,
        })),
      });
      navigate({
        to: "/shopping/$sessionId",
        params: { sessionId: session.id },
      });
    }
  };

  const isPending = createSession.isPending || addSessionItem.isPending;

  const renderTodoRow = (todo: Todo) => {
    const picked = pickedItems.get(todo.id);
    if (!picked) return null;

    return (
      <li
        key={todo.id}
        className="flex h-[4.25rem] items-center gap-3 border-b"
      >
        <RoundCheckButton
          checked={picked.selected}
          onClick={() => toggleItem(todo.id)}
          variant="clipboard"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-foreground">{todo.title}</p>
          {todo.quantity && (
            <span className="text-sm text-muted-foreground">
              {todo.quantity}
            </span>
          )}
        </div>
        <select
          value={picked.venueName}
          onChange={(e) => changeVenue(todo.id, e.target.value)}
          className="rounded border border-border bg-background px-2 py-1 text-sm"
        >
          <optgroup label="今日行く店">
            {venues.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </optgroup>
          {picked.venueName && !venues.includes(picked.venueName) && (
            <optgroup label="その他">
              <option value={picked.venueName}>{picked.venueName}</option>
            </optgroup>
          )}
        </select>
      </li>
    );
  };

  if (todosLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-muted-foreground">読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-3 border-b px-4 py-3">
        <button
          type="button"
          onClick={() =>
            isAddMode
              ? navigate({
                  to: "/shopping/$sessionId",
                  params: { sessionId },
                })
              : navigate({ to: "/shopping/new" })
          }
          className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="戻る"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold text-foreground">
          {isAddMode ? "アイテムを追加" : "買うものを選ぶ"}
        </h1>
      </header>

      <div className="flex-1 overflow-y-auto px-4">
        {venues.map((venue) => {
          const venueTodos = matchedTodos.filter(
            (t) => t.suggestedVenue === venue,
          );
          if (venueTodos.length === 0) return null;
          return (
            <div key={venue}>
              <h2 className="mt-4 mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <Tag className="h-4 w-4" />
                {venue}
              </h2>
              <ul>{venueTodos.map(renderTodoRow)}</ul>
            </div>
          );
        })}

        {otherTodos.length > 0 && (
          <>
            <div className="mt-6 mb-2 border-t pt-4">
              <h2 className="text-sm font-semibold text-muted-foreground">
                買い忘れない？
              </h2>
            </div>
            <ul>{otherTodos.map(renderTodoRow)}</ul>
          </>
        )}

        {uncheckedTodos.length === 0 && (
          <p className="py-8 text-center text-muted-foreground">
            未完了の TODO がありません
          </p>
        )}
      </div>

      <div className="border-t px-4 py-3">
        <button
          type="button"
          onClick={handleStart}
          disabled={selectedItems.length === 0 || isPending}
          className="w-full rounded-full bg-primary py-3 text-base font-medium text-primary-foreground disabled:opacity-50"
        >
          {isPending
            ? "処理中..."
            : isAddMode
              ? `追加する（${selectedItems.length}件）`
              : `買い物を始める（${selectedItems.length}件）`}
        </button>
      </div>
    </div>
  );
}
